import React, { useEffect, useState } from 'react';
import { CalendarRange, Plus, Pencil, Trash2, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { MS_STATUS_META } from '../mock/data';
import { PageHeader, StatusBadge, Modal, Field } from '../components/common';
import { useRealtime } from '../realtime/RealtimeContext';
import api from '../api/client';

const STATUSES = ['SCHEDULED', 'IN_TRANSIT', 'DELIVERED'];
const EMPTY = { material: '', qty: '', supplier: '', eta: '', job: '—', status: 'SCHEDULED' };

export default function MaterialSchedule() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.listMatSchedule().then(setItems).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useRealtime(() => load());

  const openCreate = () => { setForm(EMPTY); setModal({ mode: 'create' }); };
  const openEdit = (m) => { setForm({ ...m }); setModal({ mode: 'edit' }); };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        const created = await api.createMatSchedule(form);
        setItems((p) => [...p, created]);
        toast.success(`${created.id} added`);
      } else {
        const upd = await api.updateMatSchedule(form.id, form);
        setItems((p) => p.map((x) => (x.id === upd.id ? upd : x)));
        toast.success(`${upd.id} updated`);
      }
      setModal(null);
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const changeStatus = async (row, status) => {
    setItems((p) => p.map((x) => (x.id === row.id ? { ...x, status } : x)));
    try { await api.updateMatSchedule(row.id, { status }); toast.success(`${row.id} → ${status.replace('_', ' ')}`); }
    catch (e) { toast.error('Update failed'); load(); }
  };

  const remove = async (row) => {
    setItems((p) => p.filter((x) => x.id !== row.id));
    try { await api.deleteMatSchedule(row.id); toast.success(`${row.id} removed`); }
    catch (e) { toast.error('Delete failed'); load(); }
  };

  const approve = async (row) => {
    setItems((p) => p.map((x) => (x.id === row.id ? { ...x, draft: false } : x)));
    try { await api.approveMatSchedule(row.id); toast.success(`${row.id} approved → Scheduled`); }
    catch (e) { toast.error('Approve failed'); load(); }
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={CalendarRange} title="Material Schedule" subtitle={`${items.length} inbound deliveries`}
        right={<button className="kmt-btn-gold flex items-center gap-2" onClick={openCreate}><Plus size={16} />Add Delivery</button>} />
      <div className="p-8">
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <div className="kmt-panel overflow-x-auto">
            <table className="w-full kmt-table">
              <thead><tr>
                <th className="kmt-th">Ref</th><th className="kmt-th">Material</th><th className="kmt-th">Qty</th><th className="kmt-th">Supplier</th><th className="kmt-th">ETA</th><th className="kmt-th">Linked Job</th><th className="kmt-th">Status</th><th className="kmt-th">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id}>
                    <td className="kmt-td font-mono-k" data-label="Ref" style={{ color: 'var(--kmt-gold)' }}>
                      {m.id}
                      {m.draft && <span className="kmt-badge s-hold ml-2" style={{ fontSize: 8 }}>Draft</span>}
                    </td>
                    <td className="kmt-td" data-label="Material">{m.material}</td>
                    <td className="kmt-td font-mono-k" data-label="Qty">{m.qty}</td>
                    <td className="kmt-td" data-label="Supplier">{m.supplier}</td>
                    <td className="kmt-td" data-label="ETA" style={{ color: 'var(--kmt-muted)' }}>{m.eta}</td>
                    <td className="kmt-td font-mono-k" data-label="Linked Job" style={{ color: m.job === '—' ? 'var(--kmt-muted-2)' : 'var(--kmt-text)' }}>{m.job}</td>
                    <td className="kmt-td" data-label="Status">
                      <select className="kmt-input" style={{ padding: '5px 8px', fontSize: 12, width: 140 }}
                        value={m.status} onChange={(e) => changeStatus(m, e.target.value)}>
                        {STATUSES.map((s) => <option key={s} value={s}>{(MS_STATUS_META[s] || {}).label || s}</option>)}
                      </select>
                    </td>
                    <td className="kmt-td" data-label="Actions">
                      <div className="flex gap-2 items-center">
                        {m.draft && (
                          <button onClick={() => approve(m)} title="Approve draft"
                            className="kmt-btn-ghost text-xs flex items-center gap-1.5" style={{ padding: '5px 9px', color: 'var(--kmt-green)' }}>
                            <CheckCircle2 size={13} />Approve
                          </button>
                        )}
                        <button onClick={() => openEdit(m)} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-gold)]"><Pencil size={15} /></button>
                        <button onClick={() => remove(m)} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-red)]"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)}
        title={modal?.mode === 'create' ? 'Add Delivery' : `Edit ${form.id}`}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="kmt-btn-gold" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        </>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-2">
            <Field label="Material">
              <input className="kmt-input" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} />
            </Field>
          </div>
          <Field label="Quantity">
            <input className="kmt-input" value={form.qty} placeholder="400 kg" onChange={(e) => setForm({ ...form, qty: e.target.value })} />
          </Field>
          <Field label="Supplier">
            <input className="kmt-input" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          </Field>
          <Field label="ETA">
            <input className="kmt-input" value={form.eta} placeholder="Aug 24, 08:00" onChange={(e) => setForm({ ...form, eta: e.target.value })} />
          </Field>
          <Field label="Linked Job">
            <input className="kmt-input" value={form.job} placeholder="JOB-XXXX or —" onChange={(e) => setForm({ ...form, job: e.target.value })} />
          </Field>
          <div className="col-span-2">
            <Field label="Status">
              <select className="kmt-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{(MS_STATUS_META[s] || {}).label || s}</option>)}
              </select>
            </Field>
          </div>
        </div>
      </Modal>
    </div>
  );
}
